/**
 * /hospitality/fnb/tronc — Tips & Tronc + banquet event revenue.
 *
 * Mirrors an F&B back office: the tip-pool (tronc) distribution by outlet —
 * declared tips + service charges pooled and apportioned over tipped hours —
 * plus the booked banquet & event revenue with deposits and balances due.
 *
 * Money fields arrive as decimal strings; `money()` formats them.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatCard,
  Panel,
  StatusPill,
  money,
} from "@/components/hospitality/ui";
import type { PillTone } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/fnb/tronc")({
  component: FnbTroncPage,
});

// ── Types — matches GET /api/hospitality/fnb/tronc contract ────────────────
interface FnbTroncOutlet {
  code: string;
  name: string;
  declared_tips: string;
  service_charges: string;
  pool_total: string;
  tipped_hours: string;
  participants: number;
  per_hour_rate: string;
  status: string;
}
interface FnbTroncTotals {
  declared_tips: string;
  service_charges: string;
  pool_total: string;
  tipped_hours: string;
  participants: number;
  blended_per_hour: string;
}
interface FnbTroncResult {
  property: string;
  business_date: string | null;
  by_outlet: FnbTroncOutlet[];
  totals: FnbTroncTotals | null;
  message?: string;
}

// ── Types — matches GET /api/hospitality/fnb/banquets contract ─────────────
interface FnbBanquetEvent {
  event_name: string;
  client: string;
  event_date: string;
  covers: number;
  revenue: string;
  deposit_received: string;
  balance_due: string;
  status: string;
}
interface FnbBanquetTotals {
  booked_revenue: string;
  deposits_on_file: string;
  balance_due: string;
}
interface FnbBanquetsResult {
  property: string;
  events: FnbBanquetEvent[];
  count: number;
  upcoming_count: number;
  totals: FnbBanquetTotals;
}

// Tronc pool status: distributed → success/green, pending → open/amber.
function troncTone(status: string): PillTone {
  return status === "distributed" ? "success" : "open";
}

// Banquet status: completed → success, confirmed → info, booked → neutral/amber.
function banquetTone(status: string): PillTone {
  if (status === "completed") return "success";
  if (status === "confirmed") return "info";
  return "open";
}

function FnbTroncPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const troncQ = useQuery({
    queryKey: ["hospitality-fnb-tronc"],
    queryFn: () => api<FnbTroncResult>("/api/hospitality/fnb/tronc"),
  });
  const banquetsQ = useQuery({
    queryKey: ["hospitality-fnb-banquets"],
    queryFn: () => api<FnbBanquetsResult>("/api/hospitality/fnb/banquets"),
  });

  const data = troncQ.data;
  const banquets = banquetsQ.data;
  const loading = troncQ.isLoading || banquetsQ.isLoading;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Tips & Tronc"
        subtitle={
          data
            ? `${data.property}${data.business_date ? ` · ${data.business_date}` : ""}`
            : "Tip-pool distribution + banquet & event revenue"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {loading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!loading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              Tronc pools populate once tips and service charges are imported for a business date.
            </span>
          </div>
        )}

        {!loading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Tip pool total" value={money(data.totals?.pool_total ?? null, 0)} />
              <StatCard label="Declared tips" value={money(data.totals?.declared_tips ?? null, 0)} />
              <StatCard
                label="Service charges"
                value={money(data.totals?.service_charges ?? null, 0)}
              />
              <StatCard
                label="Blended per hour"
                value={money(data.totals?.blended_per_hour ?? null)}
              />
            </div>

            {/* Tip pools by outlet */}
            <Panel label="Tip pools by outlet">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Outlet</th>
                    <th className="text-right py-1.5 px-3">Declared tips</th>
                    <th className="text-right py-1.5 px-3">Service charges</th>
                    <th className="text-right py-1.5 px-3">Pool total</th>
                    <th className="text-right py-1.5 px-3">Tipped hrs</th>
                    <th className="text-right py-1.5 px-3">Participants</th>
                    <th className="text-right py-1.5 px-3">Per-hour rate</th>
                    <th className="text-right py-1.5 pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_outlet.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-12 text-center text-slate-500 dark:text-slate-400"
                      >
                        No tip pools for this business date.
                      </td>
                    </tr>
                  )}
                  {data.by_outlet.map((o) => (
                    <tr key={o.code} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200">{o.name}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(o.declared_tips)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(o.service_charges)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(o.pool_total)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {o.tipped_hours}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {o.participants.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(o.per_hour_rate)}
                      </td>
                      <td className="py-1.5 pl-3 text-right">
                        <StatusPill tone={troncTone(o.status)}>{o.status}</StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            {/* Banquet & event revenue */}
            {banquets && (
              <Panel label="Banquet & event revenue">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3">
                  <StatCard label="Booked revenue" value={money(banquets.totals.booked_revenue, 0)} />
                  <StatCard
                    label="Deposits on file"
                    value={money(banquets.totals.deposits_on_file, 0)}
                  />
                  <StatCard
                    label="Balance due"
                    value={money(banquets.totals.balance_due, 0)}
                    tone="danger"
                  />
                  <StatCard label="Upcoming" value={String(banquets.upcoming_count)} />
                </div>

                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left py-1.5 pr-3">Event</th>
                      <th className="text-left py-1.5 px-3">Date</th>
                      <th className="text-right py-1.5 px-3">Covers</th>
                      <th className="text-right py-1.5 px-3">Revenue</th>
                      <th className="text-right py-1.5 px-3">Deposit</th>
                      <th className="text-right py-1.5 px-3">Balance</th>
                      <th className="text-right py-1.5 pl-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banquets.events.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-12 text-center text-slate-500 dark:text-slate-400"
                        >
                          No banquet or event bookings.
                        </td>
                      </tr>
                    )}
                    {banquets.events.map((e, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3">
                          <div className="text-slate-800 dark:text-slate-200">{e.event_name}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {e.client}
                          </div>
                        </td>
                        <td className="py-1.5 px-3 tabular-nums text-xs text-slate-600 dark:text-slate-300">
                          {e.event_date}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                          {e.covers.toLocaleString()}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                          {money(e.revenue)}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                          {money(e.deposit_received)}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                          {money(e.balance_due)}
                        </td>
                        <td className="py-1.5 pl-3 text-right">
                          <StatusPill tone={banquetTone(e.status)}>{e.status}</StatusPill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
